/**
 * @file The calm "Something went wrong" card shown when a render crashes.
 *
 * Deliberately shows NO error message or stack: either can carry internals. The error is handed to
 * `reportClientError` once, on mount. Both actions are plain links/reloads (hard navigation) so a
 * broken in-memory state cannot survive them.
 */
import { useEffect } from "react";
import { reportClientError } from "../report-error";
import { Card } from "./ui";

export function ErrorCard({ error }: { readonly error: unknown }) {
  useEffect(() => {
    reportClientError(error);
  }, [error]);

  return (
    <main className="mx-auto w-full max-w-3xl flex-1 px-4 py-8">
      <Card className="items-start gap-3 px-6 py-8">
        <h1 className="text-2xl font-semibold tracking-tight">Something went wrong</h1>
        <p className="text-sm text-muted-foreground">
          We hit an unexpected problem. Reloading usually fixes it.
        </p>
        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="h-11 rounded-lg bg-primary px-4 text-sm font-medium text-primary-foreground hover:opacity-90"
          >
            Reload
          </button>
          <a
            href="/"
            className="inline-flex h-11 items-center rounded-lg border border-border bg-card px-4 text-sm font-medium hover:border-primary"
          >
            Back to dashboard
          </a>
        </div>
      </Card>
    </main>
  );
}
